import { cssBundleHref } from "@remix-run/css-bundle";
import { json, LinksFunction, redirect } from "@remix-run/node";
import {
  isRouteErrorResponse,
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useRouteError,
} from "@remix-run/react";
import * as Sentry from "@sentry/react";
import { browserProfilingIntegration } from "@sentry/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import { Replay } from "@sentry/replay";
import React, { Suspense, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChangeLanguage } from "remix-i18next/react";
import { getToast, ToastMessage } from "remix-toast";
import { toast as notify } from "sonner";
import logoSvg from "~/assets/logo.svg";
import Header from "~/components/header/Header";
import Sidebar from "~/components/sider/Sider";
import styles from "~/styles/global.css?url";
import sonnerStyles from "~/styles/sonner.css?url";
import "~/tailwind.css";
import tailwindStyles from "~/tailwind.css?url";
import { checkAuth, getMe, refreshToken } from "~/utils/auth.server";
import { ReportBug } from "./components/faq/ReportBug";
import { Toaster } from "./components/general/Sonner";
import { NotificationsProvider } from "./components/notifications/NotificationsProvider";
import { getNotifications } from "./utils/notifications.server";
import { Contact, Role } from "./utils/schema/schema";

interface Window {
  ENV?: {
    API_URL: string;
    BACKEND_URL: string;
    WEBSOCKET_URL: string;
    NOTIFICATIONS_ENABLED: boolean;
  };
}

interface EnvConfig {
  API_URL: string;
  BACKEND_URL: string;
  WEBSOCKET_URL: string;
  NOTIFICATIONS_ENABLED: boolean;
}

const ENV: EnvConfig = {
  API_URL:
    (typeof import.meta !== "undefined"
      ? import.meta.env.VITE_BACKEND_URL
      : null) || "http://localhost:8080",
  BACKEND_URL:
    (typeof import.meta !== "undefined"
      ? import.meta.env.VITE_BACKEND_URL
      : null) || "http://localhost:8080",
  WEBSOCKET_URL:
    (typeof import.meta !== "undefined"
      ? import.meta.env.VITE_WEBSOCKET_URL || import.meta.env.VITE_BACKEND_URL
      : null) || "http://localhost:8080",
  NOTIFICATIONS_ENABLED:
    typeof import.meta !== "undefined"
      ? import.meta.env.VITE_NOTIFICATIONS_ENABLED === "true"
      : false,
};

export interface LoaderData {
  locale: string;
  isAuthenticated: boolean;
  contact: Contact | undefined;
  partnerId: string;
  toast: ToastMessage | undefined;
  notifications: any;
  role: Role;
}

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: tailwindStyles },
  { rel: "stylesheet", href: sonnerStyles },
  { rel: "stylesheet", href: styles },
  ...(cssBundleHref ? [{ rel: "stylesheet", href: cssBundleHref }] : []),
];

export async function loader({ request }: { request: Request }) {
  const locale = import.meta.env.VITE_LOCALE;
  const userToken = await checkAuth(request);
  const { toast, headers } = await getToast(request);

  if (userToken) {
    const me = await getMe(request);
    if (!me) {
      const refresh = await refreshToken(request);

      return redirect("/", {
        headers: {
          "Set-Cookie": refresh,
        },
      });
    }
    const notifications = ENV.NOTIFICATIONS_ENABLED
      ? await getNotifications(me.id)
      : [];
    return json<LoaderData>(
      {
        locale,
        isAuthenticated: !!userToken,
        contact: me.contact,
        partnerId: me.id,
        toast,
        notifications,
        role: me.role,
      },
      { headers }
    );
  } else {
    return json<LoaderData>(
      {
        locale,
        isAuthenticated: false,
        contact: undefined,
        partnerId: "",
        toast,
        notifications: [],
        role: Role.Guest,
      },
      { headers }
    );
  }
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { locale, isAuthenticated, contact, partnerId, toast, notifications } =
    useLoaderData<LoaderData>() || {
      locale: "de",
      isAuthenticated: false,
      contact: undefined,
      partnerId: "",
      toast: undefined,
      notifications: [],
    };
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigation = useNavigation();

  useChangeLanguage(locale);

  useEffect(() => {
    if (toast?.type === "error") {
      notify.error(toast.message);
    }
    if (toast?.type === "success") {
      notify.success(toast.message);
    }
  }, [toast]);

  useEffect(() => {
    window.ENV = ENV;
  }, []);

  const memoizedHeader = useMemo(() => {
    return (
      <Header
        contact={contact as unknown as Contact}
        notifications={ENV.NOTIFICATIONS_ENABLED ? notifications : []}
      />
    );
  }, [contact?.id, contact?.email, notifications?.length]);

  const memoizedSidebar = useMemo(() => {
    return <Sidebar partnerId={partnerId} />;
  }, [partnerId]);

  // Loading component
  const LoadingSpinner = () => (
    <>
      {/* {navigation.state === "loading" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center pointer-events-auto">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2">Verarbeitung...</p>
          </div>
        </div>
      )} */}
    </>
  );

  return (
    <html lang={locale} dir={i18n.dir()} className="h-full">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="h-full">
       
        {isAuthenticated ? (
          <>
            {ENV.NOTIFICATIONS_ENABLED && (
              <div>
                <NotificationsProvider userId={partnerId} />
              </div>
            )}

            <div className="flex flex-col h-full" id="contentSgb">
              {memoizedHeader}
              <div className="flex flex-1">
                {memoizedSidebar}
                <div className="flex-1">
                  <main className="min-h-full p-2 bg-gray-100">
                    {children}
                  </main>
                </div>
              </div>
              <footer className="bottom-0 left-0 right-0 py-4 text-center bg-white border-t shadow-md z-50">
                <div className="container mx-auto flex justify-center gap-4">
                  <Link
                    to="/privacyPolicy"
                    className="text-gray-600 hover:text-gray-900 text-sm hover:underline hover:decoration-gray-900"
                  >
                    {i18n.t("navigation.privacyPolicy")}
                  </Link>
                  <Link
                    to="/impressum"
                    className="text-gray-600 hover:text-gray-900 text-sm hover:underline hover:decoration-gray-900"
                  >
                    {i18n.t("navigation.imprint")}
                  </Link>
                </div>
              </footer>
            </div>
          </>
        ) : (
          <>
            <main className="min-h-full">{children}</main>
            <footer className="bottom-0 left-0 right-0 py-4 text-center bg-white border-t shadow-md z-50">
              <div className="container mx-auto flex justify-center gap-4">
                <Link
                  to="/privacyPolicy"
                  className="text-gray-600 hover:text-gray-900 text-sm hover:underline hover:decoration-gray-900"
                >
                  {i18n.t("navigation.privacyPolicy")}
                </Link>
                <Link
                  to="/impressum"
                  className="text-gray-600 hover:text-gray-900 text-sm hover:underline hover:decoration-gray-900"
                >
                  {i18n.t("navigation.imprint")}
                </Link>
              </div>
            </footer>
          </>
        )}

        <ReportBug partnerId={partnerId} contact={contact?.email} />
        <Toaster />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorBoundary />}>
      <Outlet  />
    </Sentry.ErrorBoundary>
  );
}

export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError() as Error;
  captureRemixErrorBoundaryError(error);
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (
      isRouteErrorResponse(error) &&
      error.statusText === "Ihre Session ist abgelaufen"
    ) {
      const redirectTimer = setTimeout(() => {
        navigate("/login");
      }, 5000);

      return () => clearTimeout(redirectTimer);
    }
  }, [error, navigate]);

  return (
    <Suspense fallback={<div></div>}>
      <html lang="de" className="h-full">
        <head>
          <title>Oh no!</title>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="stylesheet" href={tailwindStyles} />
          <link rel="stylesheet" href={sonnerStyles} />
          <link rel="stylesheet" href={styles} />
          <Meta />
          <Links />
        </head>
        <body className="h-full overflow-y-auto bg-white">
          <div className="min-h-full flex flex-col justify-center items-center p-4">
            <div className="max-w-md w-full space-y-8 text-center">
              <h1 className="text-4xl font-bold">OH NO!</h1>
              {isRouteErrorResponse(error) &&
              error.statusText === "Ihre Session ist abgelaufen" ? (
                <p className="text-xl font-bold">
                  Der Link zum zurücksetzen des Passworts ist abgelaufen oder
                  ungültig. Bitte wende dich an unsere Partnerbetreuung für
                  weitere Unterstützung
                </p>
              ) : (
                <h2 className="text-2xl font-bold">
                  Im Partnerportal ist ein Fehler aufgetreten
                </h2>
              )}

              <img
                src={logoSvg}
                alt="error"
                className="w-full max-w-lg h-auto mx-auto"
              />

              <p className="text-xl font-bold">
                Bitte versuchen Sie es erneut, oder kontaktieren Sie den Support
                mit einer kurzen Beschreibung was passiert ist und folgender
                Fehlermeldung vom System:
              </p>
              <div className="border rounded-md p-4 overflow-x-auto">
                <pre className="whitespace-pre-wrap break-words text-left">
                  {isRouteErrorResponse(error)
                    ? `${error.status} ${error.statusText}`
                    : error instanceof Error
                    ? error.message
                    : "Unknown error"}
                </pre>
              </div>
              {isRouteErrorResponse(error) &&
                error.statusText === "Ihre Session ist abgelaufen" && (
                  <div className="text-base font-bold">
                    Der Link zum zurücksetzen des Passworts ist abgelaufen oder
                    ungültig. Bitte wende dich an unsere Partnerbetreuung für
                    weitere Unterstützung
                  </div>
                )}
            </div>
          </div>
          <Scripts />
        </body>
      </html>
    </Suspense>
  );
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    new Replay(),
    browserProfilingIntegration(),
    Sentry.captureConsoleIntegration({
      levels: ["warn", "error"],
    }),
  ],
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  tracesSampleRate: 1.0,
});
